import React, { useMemo } from "react"
import { Link, useParams } from "react-router-dom"
import { DateTime } from "luxon"
import { type ActivityChangeSet, ActivityReservationUpdated } from "@/types/activities"
import OwnerLabel from "@/components/activities/owner_label"
import ActivityBase from "@/components/activities/base"
import i18n from "@/i18n"
import { STATUS_LABELS } from "@/constants/reservations"
import Changes from "@/components/activities/changes"
import { reservationEditPath } from "@/shared/urls"

interface UpdatedProps {
  venue: any
  activity: ActivityReservationUpdated & { resource?: { __typename: string; id: string } | null }
  className?: string
}

const Updated = ({ venue, activity, className }: UpdatedProps) => {
  const { slug } = useParams<{ slug: string }>()

  const LABELS = {
    startTime: i18n.t("app.activities.reservation.field_labels.start_time"),
    endTime: i18n.t("app.activities.reservation.field_labels.end_time"),
    peopleAmount: i18n.t("app.activities.reservation.field_labels.people_amount"),
    tableId: i18n.t("app.activities.reservation.field_labels.table"),
    status: i18n.t("app.activities.reservation.field_labels.status"),
    date: i18n.t("app.activities.reservation.field_labels.date"),
  }

  const formatters = useMemo(() => {
    const getTableName = (id) => venue.tables.find((table) => Number(table.id) === id)?.name || "(deleted)"

    return {
      startTime: (value) => DateTime.fromISO(value).toLocaleString(DateTime.TIME_24_SIMPLE),
      endTime: (value) => DateTime.fromISO(value).toLocaleString(DateTime.TIME_24_SIMPLE),
      date: (value) => value.toFormat(i18n.t("app.date.formats.month_day")),
      tableId: getTableName,
      status: (value) => STATUS_LABELS[value],
    }
  }, [venue.tables])

  let changes = activity.parameters.changes as ActivityChangeSet

  if (activity.parameters.changes.startTime) {
    const startTimeFrom = DateTime.fromISO(activity.parameters.changes.startTime.from)
    const startTimeTo = DateTime.fromISO(activity.parameters.changes.startTime.to)

    if (startTimeFrom.toISODate() !== startTimeTo.toISODate()) {
      changes = { ...changes, date: { from: startTimeFrom, to: startTimeTo } }
    }
  }

  const resourceLink =
    activity.resource?.__typename === "Reservation" && slug ? (
      <Link to={reservationEditPath(slug, activity.resource.id)} className="text-blue-500 hover:underline">
        #{activity.resource.id}
      </Link>
    ) : null

  return (
    <ActivityBase
      data-testid="panel-activity-reservation-updated"
      activityKey={activity.key}
      createdAt={activity.createdAt}
      text={
        <>
          <div>
            {i18n.react("app.activities.reservation.updated.description", {
              owner: <OwnerLabel owner={activity.owner} />,
              resourceLink,
            })}
            <Changes changes={changes} labels={LABELS} formatters={formatters} />
          </div>
        </>
      }
      className={className}
    />
  )
}

export default Updated
